/**
 * assigneeGuardError — A2-S6 shared department/role guard error mapping.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Single source of truth for turning the backend's assignee department/role
 * guard rejection into a readable hint. Consumed by BOTH doctor-workflow
 * create surfaces that submit an un-pre-filtered assignee:
 *   - Smart Workflow  → widget/QuickSearchAdd.jsx   (createPlan)
 *   - Attach path     → AttachPlanModal.jsx          (attachToVisit)
 *
 * Neither surface pre-filters the assignee client-side (single source of
 * truth lives on the server-side assignable-staff list + the write guards),
 * so both can still submit an out-of-department / role-ineligible assignee
 * that the guard rejects:
 *   - 403  → assignee is in a different department
 *   - 400  → assignee's role isn't eligible for this procedure (e.g. a nurse
 *            on a non-nurse_allowed procedure)
 *
 * When the server sends a specific `detail`/`message`, our API translators
 * surface it verbatim and it passes straight through here. But a bare 403/400
 * with NO specific detail collapses to buildApiError's generic string
 * ("You do not have permission…" / "Invalid request…") that hides the real
 * cause — so on those generic fallbacks we map to the eligibility hint.
 * Non-guard errors (500 etc.) are untouched.
 */

// The generic fallback strings both API translators emit for a bare 403/400
// (see patientPlanApi.buildApiError + treatmentPlanMasterApi.buildApiError —
// kept in sync with those switch cases). A server `detail` never matches
// these, so a specific message still passes through verbatim.
export const GENERIC_403 = 'You do not have permission for this action.';
export const GENERIC_400 = 'Invalid request — please check the form.';

export const ELIGIBILITY_HINT =
  "Selected staff isn't eligible for this procedure or isn't in its department.";

/**
 * Map an API error result `{ success, error, status, ... }` to a display
 * string. Bare 403/400 guard rejections → eligibility hint; specific server
 * detail → passthrough; everything else → server message or the caller's
 * fallback.
 *
 * @param {object} result   API-shaped error result (from handleError/fail).
 * @param {string} [fallback='Could not add procedure.']
 */
export function addProcedureErrorMessage(result, fallback = 'Could not add procedure.') {
  const status = result?.status;
  const serverMsg = result?.error;
  if (
    (status === 403 || status === 400)
    && (!serverMsg || serverMsg === GENERIC_403 || serverMsg === GENERIC_400)
  ) {
    return ELIGIBILITY_HINT;
  }
  return serverMsg || fallback;
}
